import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useT, useFormatDate } from '../i18n';

interface AggregateComm {
  id: number;
  participant_id: number;
  participant_first_name: string | null;
  participant_last_name: string | null;
  measure_id: number | null;
  measure_label: string | null;
  contract_number: string | null;
  kind: string;
  related_adjustment_id: number | null;
  to_name: string | null;
  to_email: string | null;
  subject: string;
  body: string;
  generated_at: string;
  generated_by: string | null;
  sent_at: string | null;
  sent_marked_by: string | null;
}

type StatusFilter = 'unsent' | 'sent' | 'all';

function KindBadge({ kind, t }: { kind: string; t: (key: string, params?: Record<string, string | number>) => string }) {
  const label = kind === 'progression_adjustment' ? t('comms.kindAdjustment') : kind;
  return <span className="badge badge-sm">{label}</span>;
}

export function CommunicationsPage() {
  const { t } = useT();
  const fd = useFormatDate();
  const [comms, setComms] = useState<AggregateComm[]>([]);
  const [counts, setCounts] = useState<{ unsent: number; sent: number; total: number }>({ unsent: 0, sent: 0, total: 0 });
  const [status, setStatus] = useState<StatusFilter>('unsent');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<{ subject: string; body: string }>({ subject: '', body: '' });
  const [confirming, setConfirming] = useState<
    { kind: 'mark-sent' | 'delete'; comm: AggregateComm } | null
  >(null);

  function startEdit(c: AggregateComm) {
    setEditingId(c.id);
    setEditDraft({ subject: c.subject, body: c.body });
    setExpandedId(c.id);
  }
  function cancelEdit() {
    setEditingId(null);
    setEditDraft({ subject: '', body: '' });
  }
  async function saveEdit(c: AggregateComm) {
    setActingId(c.id);
    try {
      await api.updateCommunication(c.id, { subject: editDraft.subject, body: editDraft.body });
      setEditingId(null);
      await reload();
    } catch (e: any) {
      setError(e?.message || t('comms.errSave'));
    } finally {
      setActingId(null);
    }
  }

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getAllCommunications({ status, limit: 500 }) as any;
      setComms(data?.communications ?? []);
      setCounts(data?.counts ?? { unsent: 0, sent: 0, total: 0 });
    } catch (e: any) {
      setError(e?.message || t('comms.errLoad'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const filtered = comms.filter(c => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    const name = `${c.participant_last_name ?? ''} ${c.participant_first_name ?? ''}`.toLowerCase();
    return name.includes(q)
      || (c.to_name ?? '').toLowerCase().includes(q)
      || (c.to_email ?? '').toLowerCase().includes(q)
      || c.subject.toLowerCase().includes(q);
  });

  async function copyBody(c: AggregateComm) {
    const text = `${t('comms.copySubjectPrefix')}: ${c.subject}\n\n${c.body}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(c.id);
      setTimeout(() => setCopiedId(prev => (prev === c.id ? null : prev)), 1500);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* ignore */ }
      document.body.removeChild(ta);
      setCopiedId(c.id);
      setTimeout(() => setCopiedId(prev => (prev === c.id ? null : prev)), 1500);
    }
  }

  async function performAction() {
    if (!confirming) return;
    const c = confirming.comm;
    const kind = confirming.kind;
    setActingId(c.id);
    try {
      if (kind === 'mark-sent') await api.markCommunicationSent(c.id);
      else await api.deleteCommunication(c.id);
      setConfirming(null);
      await reload();
    } catch (e: any) {
      setError(e?.message || (kind === 'mark-sent' ? t('comms.errMarkSent') : t('comms.errDelete')));
    } finally {
      setActingId(null);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', marginBottom: 'var(--s12)', flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 'var(--t-17)', fontWeight: 700 }}>{t('comms.pageTitle')}</h1>
        <span className="stat-pill" title={t('comms.unsentTooltip')}>{t('comms.unsentCount', { n: counts.unsent })}</span>
        <span className="stat-pill" title={t('comms.sentTooltip')}>{t('comms.sentCount', { n: counts.sent })}</span>
      </div>

      {/* Filter bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 'var(--s8)',
        marginBottom: 'var(--s12)', flexWrap: 'wrap',
      }}>
        <div style={{ display: 'inline-flex', borderRadius: 'var(--r1)', overflow: 'hidden', border: '1px solid var(--line)' }}>
          {(['unsent', 'sent', 'all'] as StatusFilter[]).map(s => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              style={{
                padding: 'var(--s4) var(--s12)',
                background: status === s ? 'var(--accent)' : 'var(--surface)',
                color: status === s ? 'var(--surface)' : 'var(--ink-2)',
                border: 'none',
                borderLeft: 'none',
                cursor: 'pointer',
                fontSize: 'var(--t-12)',
                fontWeight: 600,
              }}
            >
              {s === 'unsent' ? t('comms.tabUnsent') : s === 'sent' ? t('comms.tabSent') : t('comms.tabAll')}
            </button>
          ))}
        </div>

        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('comms.searchPlaceholder')}
          style={{
            flex: 1, minWidth: 240, maxWidth: 480,
            padding: 'var(--s4) var(--s8)',
            borderRadius: 'var(--r1)',
            border: '1px solid var(--line)',
            background: 'var(--surface)',
            fontSize: 'var(--t-12)',
            color: 'var(--ink-1)',
          }}
        />
      </div>

      {error && (
        <div style={{ color: 'var(--err)', marginBottom: 'var(--s8)' }}>{error}</div>
      )}

      {loading && comms.length === 0 ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--s32)' }}><div className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 'var(--s24)', textAlign: 'center', color: 'var(--ink-3)' }}>
          {status === 'unsent'
            ? t('comms.emptyUnsent')
            : status === 'sent'
            ? t('comms.emptySent')
            : t('comms.emptyAll')}
        </div>
      ) : (
        <table className="table table-compact" style={{ tableLayout: 'auto' }}>
          <thead>
            <tr>
              <th>{t('comms.colParticipant')}</th>
              <th>{t('comms.colMeasure')}</th>
              <th>{t('comms.colType')}</th>
              <th>{t('comms.colRecipient')}</th>
              <th>{t('comms.colGenerated')}</th>
              <th>{t('comms.colStatus')}</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c, i) => {
              const isOpen = expandedId === c.id;
              const isSent = c.sent_at != null;
              return (
                <>
                  <tr
                    key={c.id}
                    style={{
                      backgroundColor: i % 2 === 1 ? 'var(--accent-dim)' : 'var(--surface)',
                      cursor: 'pointer',
                    }}
                    onClick={() => setExpandedId(isOpen ? null : c.id)}
                  >
                    <td style={{ fontWeight: 500 }}>
                      <a
                        href={`#participant?id=${c.participant_id}${c.measure_id ? `&measure=${c.measure_id}` : ''}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: 'inherit', textDecoration: 'none' }}
                      >
                        {c.participant_last_name} {c.participant_first_name}
                      </a>
                    </td>
                    <td style={{ fontSize: 'var(--t-11)', color: 'var(--ink-2)' }}>
                      {c.measure_label || '—'}
                      {c.contract_number && (
                        <span style={{ color: 'var(--ink-3)', marginLeft: 'var(--s4)' }}>
                          · {c.contract_number}
                        </span>
                      )}
                    </td>
                    <td><KindBadge kind={c.kind} t={t} /></td>
                    <td style={{ fontSize: 'var(--t-12)' }}>
                      {c.to_name || '—'}
                      {c.to_email && (
                        <span style={{ color: 'var(--ink-3)', marginLeft: 'var(--s4)' }}>
                          &lt;{c.to_email}&gt;
                        </span>
                      )}
                    </td>
                    <td style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>
                      {c.generated_at ? fd.dateTime(c.generated_at) : '—'}
                      {c.generated_by && (
                        <div style={{ color: 'var(--ink-3)' }}>{t('comms.generatedBy', { name: c.generated_by })}</div>
                      )}
                    </td>
                    <td>
                      {isSent ? (
                        <span className="badge badge-sm badge-ok" title={c.sent_marked_by ? t('comms.markedByTooltip', { name: c.sent_marked_by }) : undefined}>
                          {t('comms.sentBadge', { date: c.sent_at ? fd.dateTime(c.sent_at) : '—' })}
                        </span>
                      ) : (
                        <span className="badge badge-sm">{t('comms.draftBadge')}</span>
                      )}
                    </td>
                    <td style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)' }}>
                      {isOpen ? '▼' : '▸'}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr key={`${c.id}-detail`}>
                      <td colSpan={7} style={{ padding: 0 }}>
                        <div style={{
                          padding: 'var(--s12)',
                          background: 'var(--bg)',
                          borderTop: '1px solid var(--line)',
                          borderBottom: '1px solid var(--line)',
                        }}>
                          {editingId === c.id ? (
                            <>
                              <div style={{ fontSize: 'var(--t-12)', color: 'var(--ink-2)', marginBottom: 'var(--s4)' }}>
                                <strong>{t('comms.subjectLabel')}</strong>
                              </div>
                              <input
                                type="text"
                                value={editDraft.subject}
                                onChange={(e) => setEditDraft(d => ({ ...d, subject: e.target.value }))}
                                style={{
                                  width: '100%', boxSizing: 'border-box',
                                  padding: 'var(--s4) var(--s8)',
                                  borderRadius: 'var(--r2)',
                                  border: '1px solid var(--accent)',
                                  background: 'var(--surface)',
                                  fontSize: 'var(--t-12)',
                                  color: 'var(--ink-1)',
                                  marginBottom: 'var(--s8)',
                                }}
                              />
                              <textarea
                                value={editDraft.body}
                                onChange={(e) => setEditDraft(d => ({ ...d, body: e.target.value }))}
                                rows={Math.min(25, Math.max(10, editDraft.body.split('\n').length + 1))}
                                style={{
                                  width: '100%', boxSizing: 'border-box',
                                  fontFamily: 'var(--font-mono, ui-monospace, monospace)',
                                  fontSize: 'var(--t-11)',
                                  color: 'var(--ink-1)',
                                  padding: 'var(--s8)',
                                  borderRadius: 'var(--r2)',
                                  border: '1px solid var(--accent)',
                                  background: 'var(--surface)',
                                  resize: 'vertical',
                                }}
                              />
                            </>
                          ) : (
                            <>
                              <div style={{ fontSize: 'var(--t-12)', color: 'var(--ink-2)', marginBottom: 'var(--s8)' }}>
                                <strong>{t('comms.subjectLabel')}</strong> {c.subject}
                              </div>
                              <pre style={{
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'var(--font-mono, ui-monospace, monospace)',
                                fontSize: 'var(--t-11)',
                                color: 'var(--ink-1)',
                                margin: 0,
                                background: 'var(--surface)',
                                padding: 'var(--s8)',
                                borderRadius: 'var(--r2)',
                                border: '1px solid var(--line)',
                              }}>{c.body}</pre>
                            </>
                          )}
                          <div style={{ marginTop: 'var(--s8)', display: 'flex', gap: 'var(--s8)', flexWrap: 'wrap' }}>
                            <button type="button" onClick={() => copyBody(c)} className="sort-btn">
                              📋 {copiedId === c.id ? t('comms.copied') : t('comms.copyButton')}
                            </button>
                            {c.to_email && (
                              <a
                                href={`mailto:${encodeURIComponent(c.to_email)}?subject=${encodeURIComponent(c.subject)}&body=${encodeURIComponent(c.body)}`}
                                className="sort-btn"
                                style={{ textDecoration: 'none' }}
                              >
                                ✉ {t('comms.openInMail')}
                              </a>
                            )}
                            {!isSent && editingId !== c.id && (
                              <>
                                <button type="button" onClick={() => startEdit(c)} disabled={actingId === c.id} className="sort-btn">
                                  ✎ {t('common.edit')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setConfirming({ kind: 'mark-sent', comm: c })}
                                  disabled={actingId === c.id}
                                  style={{
                                    padding: 'var(--s4) var(--s12)',
                                    borderRadius: 'var(--r2)',
                                    border: 'none',
                                    background: 'var(--ok)',
                                    color: 'var(--surface)',
                                    fontSize: 'var(--t-12)',
                                    fontWeight: 600,
                                    cursor: actingId === c.id ? 'wait' : 'pointer',
                                  }}
                                >
                                  ✓ {t('comms.markSent')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setConfirming({ kind: 'delete', comm: c })}
                                  disabled={actingId === c.id}
                                  style={{
                                    padding: 'var(--s4) var(--s8)',
                                    borderRadius: 'var(--r2)',
                                    border: '1px solid var(--line)',
                                    background: 'var(--surface)',
                                    color: 'var(--err)',
                                    fontSize: 'var(--t-11)',
                                    cursor: actingId === c.id ? 'wait' : 'pointer',
                                  }}
                                >
                                  {t('common.delete')}
                                </button>
                              </>
                            )}
                            {editingId === c.id && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => saveEdit(c)}
                                  disabled={actingId === c.id || !editDraft.subject.trim() || !editDraft.body.trim()}
                                  style={{
                                    padding: 'var(--s4) var(--s12)',
                                    borderRadius: 'var(--r2)',
                                    border: 'none',
                                    background: 'var(--accent)',
                                    color: 'var(--surface)',
                                    fontSize: 'var(--t-12)',
                                    fontWeight: 600,
                                    cursor: actingId === c.id ? 'wait' : 'pointer',
                                  }}
                                >
                                  💾 {t('common.save')}
                                </button>
                                <button type="button" onClick={cancelEdit} disabled={actingId === c.id} className="sort-btn">
                                  {t('common.cancel')}
                                </button>
                              </>
                            )}
                            <a
                              href={`#participant?id=${c.participant_id}${c.measure_id ? `&measure=${c.measure_id}` : ''}`}
                              style={{ marginLeft: 'auto', alignSelf: 'center', fontSize: 'var(--t-11)', color: 'var(--accent)' }}
                            >
                              {t('comms.openParticipant')}
                            </a>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      )}

      <ConfirmDialog
        open={confirming != null}
        title={confirming?.kind === 'mark-sent' ? t('comms.confirmMarkSentTitle') : t('comms.confirmDeleteTitle')}
        message={
          confirming?.kind === 'mark-sent'
            ? t('comms.confirmMarkSentMsg')
            : t('comms.confirmDeleteMsg')
        }
        confirmLabel={confirming?.kind === 'mark-sent' ? t('comms.confirmMarkSentLabel') : t('common.delete')}
        tone={confirming?.kind === 'delete' ? 'danger' : 'ok'}
        busy={actingId != null}
        onConfirm={performAction}
        onCancel={() => setConfirming(null)}
      />
    </div>
  );
}
